import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { ThemeProvider as MuiThemeProvider, useTheme as useMuiTheme } from '@mui/material/styles';
import { Box, Typography, Button, Stack, Popper, Paper, ClickAwayListener } from '@mui/material';
import { RestartAlt, Check, ArrowForward } from '@mui/icons-material';
import { lightTheme, darkTheme, makeAccentTheme } from '../utils/theme';

const ThemeContext = createContext();

const ACCENT_KEY = 'theme_accent_color';   // hex string or '' for "no override"

export function ThemeProvider({ children }) {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  // User-picked accent color. Empty string = no override, use the default
  // light/dark theme. When set, primary + secondary colors come from this
  // value while the canvas (background/text) still tracks dark/light mode.
  const [accentColor, setAccentColor] = useState(() => localStorage.getItem(ACCENT_KEY) || '');

  // Build the active theme. If user picked an accent color, use it; otherwise
  // fall back to the original light/dark themes.
  const theme = accentColor
    ? makeAccentTheme(accentColor, isDarkMode ? 'dark' : 'light')
    : (isDarkMode ? darkTheme : lightTheme);

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.body.style.backgroundColor = theme.palette.background.default;
  }, [isDarkMode, theme.palette.background.default]);

  useEffect(() => {
    if (accentColor) localStorage.setItem(ACCENT_KEY, accentColor);
    else localStorage.removeItem(ACCENT_KEY);
  }, [accentColor]);

  const toggleTheme = () => setIsDarkMode((prev) => !prev);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, accentColor, setAccentColor }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);

// Reusable hover-popover that renders the accent color picker. Drop this
// next to (or wrapping) the dark/light IconButton; the popover opens on
// hover and closes a moment after the cursor leaves both the trigger and
// the popover itself.
//
// Usage:
//   <AccentColorHoverWrapper>
//     <IconButton onClick={toggleTheme}>{icon}</IconButton>
//   </AccentColorHoverWrapper>
export function AccentColorHoverWrapper({ children }) {
  const { accentColor, setAccentColor } = useTheme();
  const muiTheme = useMuiTheme();
  const [open, setOpen] = useState(false);
  // Local "preview" of the in-flight color. The native <input type="color">
  // fires onChange on every hue tick, so we keep it in component state
  // (cheap) and only commit to the global accent — which rebuilds the MUI
  // theme and re-renders the whole app — when the user clicks Apply.
  const [pendingColor, setPendingColor] = useState(accentColor);
  useEffect(() => { setPendingColor(accentColor); }, [accentColor]);
  // "Engaged" = the user has clicked into the custom color picker this
  // session, even if they haven't picked a value yet. While engaged, the
  // hover-away auto-close is suppressed — they're working, not browsing.
  // Reset when the popover actually closes so the next hover starts fresh.
  const [engaged, setEngaged] = useState(false);
  useEffect(() => { if (!open) setEngaged(false); }, [open]);
  const wrapRef = useRef(null);
  const colorInputRef = useRef(null);
  const closeTimer = useRef(null);

  const cancelClose = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  const handleColorInput = (value) => {
    setPendingColor(value);
  };

  const hasPendingChange = pendingColor && pendingColor.toLowerCase() !== (accentColor || '').toLowerCase();
  const applyPending = () => {
    if (hasPendingChange) setAccentColor(pendingColor);
    setOpen(false);
  };
  const cancelPending = () => { setPendingColor(accentColor); };

  // Hover-leave auto-close is suppressed while:
  //   - There's an unconfirmed pending change (don't lose work mid-pick), OR
  //   - The user has clicked into the picker this session (they may be in the
  //     OS color dialog and haven't selected anything yet).
  // Only an explicit Apply / Cancel / Default click or click-outside dismisses
  // the popover at that point.
  const handleMouseLeave = () => {
    if (hasPendingChange || engaged) return;
    scheduleClose();
  };

  return (
    <Box
      ref={wrapRef}
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={handleMouseLeave}
      sx={{ display: 'inline-flex' }}
    >
      {children}
      <Popper
        open={open}
        anchorEl={wrapRef.current}
        placement="bottom-end"
        modifiers={[{ name: 'offset', options: { offset: [0, 10] } }]}
        sx={{ zIndex: 1500 }}
      >
        <ClickAwayListener
          onClickAway={() => {
            // Click-outside just closes — the pendingColor is *kept* in state,
            // so when the user hovers back over the theme icon the same
            // preview is still showing and they can hit Apply. To commit
            // they must use the Apply button (or a preset).
            setOpen(false);
          }}
        >
          <Paper
            elevation={8}
            onMouseEnter={cancelClose}
            onMouseLeave={handleMouseLeave}
            sx={{
              p: 2.25, width: 264, borderRadius: 2,
              border: '1px solid', borderColor: 'divider',
              backgroundImage: (t) => t.palette.mode === 'dark'
                ? 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0))'
                : 'linear-gradient(180deg, rgba(0,0,0,0.015), rgba(0,0,0,0))',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                Accent color
              </Typography>
              {accentColor && (
                <Button
                  size="small"
                  onClick={() => setAccentColor('')}
                  startIcon={<RestartAlt sx={{ fontSize: 16 }} />}
                  sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 11, color: 'text.secondary',
                    '&:hover': { color: 'text.primary', bgcolor: 'transparent' } }}
                >
                  Default
                </Button>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.75 }}>
              Drives buttons, links, and highlights.
            </Typography>

            {/* Before → After mini-strip while previewing a custom color, so
                the user can see exactly which color they're about to replace. */}
            {hasPendingChange && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{
                  mb: 1.5, px: 1, py: 0.75, borderRadius: 1,
                  bgcolor: 'action.hover',
                  border: '1px dashed', borderColor: 'divider',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{
                    width: 18, height: 18, borderRadius: '50%',
                    bgcolor: accentColor || muiTheme.palette.primary.main,
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
                  }} />
                  <Typography variant="caption" color="text.secondary"
                    sx={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: 0.4 }}>
                    {(accentColor || 'default').toUpperCase().replace('#', '')}
                  </Typography>
                </Stack>
                <ArrowForward sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
                  <Box sx={{
                    width: 18, height: 18, borderRadius: '50%',
                    bgcolor: pendingColor,
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
                  }} />
                  <Typography variant="caption"
                    sx={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: 0.4, fontWeight: 700 }}>
                    {pendingColor.toUpperCase().replace('#', '')}
                  </Typography>
                </Stack>
              </Stack>
            )}

            {/* Custom color "pill" — swatch + hex code in a single rounded chip */}
            <Box
              component="label"
              onMouseDown={() => setEngaged(true)}
              sx={{
                position: 'relative', display: 'flex', alignItems: 'center', gap: 1.25,
                width: '100%', mb: 2,
                p: 0.75, pr: 1.5, borderRadius: 999, cursor: 'pointer',
                border: '1px solid', borderColor: 'divider',
                bgcolor: 'action.hover',
                transition: 'border-color 120ms, transform 120ms',
                '&:hover': { borderColor: 'text.secondary' },
                '&:active': { transform: 'scale(0.99)' },
              }}
            >
              <Box
                sx={{
                  width: 32, height: 32, borderRadius: '50%',
                  bgcolor: pendingColor || accentColor || muiTheme.palette.primary.main,
                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.25), 0 1px 4px rgba(0,0,0,0.18)',
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1, mb: 0.25 }}>
                  Custom
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, lineHeight: 1.2 }}>
                  {(pendingColor || accentColor || muiTheme.palette.primary.main).toUpperCase()}
                </Typography>
              </Box>
              <input
                ref={colorInputRef}
                type="color"
                value={pendingColor || accentColor || muiTheme.palette.primary.main}
                onChange={(e) => handleColorInput(e.target.value)}
                style={{
                  position: 'absolute', left: 6, top: 6,
                  width: 32, height: 32,
                  opacity: 0, cursor: 'pointer', border: 'none', padding: 0,
                }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary"
              sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10, fontWeight: 700 }}>
              Presets
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: 0.75,
              }}
            >
              {['#1b2a41', '#a0e114', '#e91e63', '#ff5722', '#4caf50', '#00bcd4', '#ff9800', '#891f76'].map((c) => {
                const isSelected = accentColor.toLowerCase() === c.toLowerCase();
                return (
                  <Box
                    key={c}
                    onClick={() => setAccentColor(c)}
                    sx={{
                      width: '100%', aspectRatio: '1 / 1', borderRadius: '50%',
                      cursor: 'pointer', bgcolor: c, position: 'relative',
                      transition: 'transform 120ms, box-shadow 120ms',
                      boxShadow: isSelected
                        ? `0 0 0 2px ${c}, 0 0 0 4px ${muiTheme.palette.background.paper}`
                        : 'inset 0 0 0 1px rgba(255,255,255,0.15)',
                      '&:hover': { transform: 'scale(1.15)' },
                      '&::after': isSelected ? {
                        content: '""',
                        position: 'absolute', inset: 0,
                        borderRadius: '50%',
                        boxShadow: `0 0 0 2px ${muiTheme.palette.text.primary}`,
                      } : {},
                    }}
                  />
                );
              })}
            </Box>

            {/* Apply / Cancel — only shown while there's an unconfirmed
                custom-color change. Picking a preset above applies instantly,
                but the custom hex picker stages the color until confirmed. */}
            {hasPendingChange && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Check />}
                  onClick={applyPending}
                  fullWidth
                  sx={{
                    bgcolor: pendingColor,
                    color: muiTheme.palette.getContrastText(pendingColor),
                    '&:hover': { bgcolor: pendingColor, filter: 'brightness(0.92)' },
                    fontWeight: 600,
                  }}
                >
                  Apply
                </Button>
                <Button size="small" variant="outlined" onClick={cancelPending} sx={{ flexShrink: 0 }}>
                  Cancel
                </Button>
              </Stack>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
